/**
 * The one place the app renders its logo.
 *
 * Artwork is *not* imported as a module. The paths come from `/meta/config`, so
 * rebranding a running deployment is a file swap plus an env var — the same
 * one-step change the app name already is — with no frontend rebuild.
 *
 * Two variants, because they solve different problems:
 *
 *   `emblem` — the square mark alone. For places that already show the app name
 *     as text beside it (sidebar, mobile header). Using the full lockup there
 *     would print the wordmark twice.
 *   `lockup` — mark + wordmark, for where the brand stands on its own and there
 *     is room for it (the login hero). It carries the name itself, so the
 *     adjacent text line is dropped rather than duplicated.
 *
 * Each resolves to a light- or dark-surface rendition from the active theme. The
 * artwork's navy ink measures ~1.5:1 on this app's dark surfaces, so the reverse
 * variant is a legibility requirement, not a preference.
 *
 * Missing or broken artwork degrades to a neutral glyph rather than a broken
 * image, so a deployment that ships no logo still renders a coherent header.
 */
import { useEffect, useState } from "react";

import { useAuth } from "@/auth/AuthContext";
import { config } from "@/config";
import { useTheme } from "@/theme/ThemeProvider";

type BrandVariant = "emblem" | "lockup";

interface BrandProps {
  variant?: BrandVariant;
  /** Rendered height in px. Width follows the artwork's aspect ratio. */
  size?: number;
  className?: string;
}

/** Join a config-supplied path onto the SPA's static root. */
function assetUrl(path: string): string {
  if (/^(https?:)?\/\//.test(path) || path.startsWith("/")) return path;
  return `${config.urlRoot.replace(/\/$/, "")}/${path}`;
}

/**
 * Stand-in when a deployment configures no artwork, or the file fails to load.
 *
 * The lockup's whole job is to carry the name, so its fallback is a text
 * wordmark — dropping to a bare glyph would leave the login screen with nothing
 * naming the app. The emblem always sits next to the name already, so a glyph
 * is all it owes.
 */
function Fallback({
  variant,
  size,
  name,
  className,
}: {
  variant: BrandVariant;
  size: number;
  name: string;
  className?: string;
}) {
  const glyph = (
    <span aria-hidden="true" style={{ fontSize: size * 0.8, lineHeight: 1 }}>
      💧
    </span>
  );
  if (variant !== "lockup") {
    return (
      <span className={className} style={{ lineHeight: 1 }}>
        {glyph}
      </span>
    );
  }
  return (
    <span className={`flex items-center gap-2 ${className ?? ""}`}>
      {glyph}
      <span className="font-semibold" style={{ fontSize: size * 0.45 }}>
        {name}
      </span>
    </span>
  );
}

export default function Brand({
  variant = "emblem",
  size = 32,
  className,
}: BrandProps) {
  const { appName, brand } = useAuth();
  const { theme } = useTheme();
  const [failed, setFailed] = useState(false);

  const path =
    variant === "lockup"
      ? theme === "dark"
        ? brand?.lockup_dark
        : brand?.lockup
      : theme === "dark"
        ? brand?.emblem_dark
        : brand?.emblem;

  // A theme flip swaps the file; give a previously-broken src another chance
  // rather than latching the fallback for the rest of the session.
  useEffect(() => setFailed(false), [path]);

  if (!path || failed) {
    return (
      <Fallback variant={variant} size={size} name={appName} className={className} />
    );
  }

  return (
    <img
      src={assetUrl(path)}
      // The lockup *is* the name in pictorial form, so it carries it for screen
      // readers. The emblem always sits beside the name in text, where a second
      // announcement would just be noise.
      alt={variant === "lockup" ? appName : ""}
      aria-hidden={variant === "lockup" ? undefined : true}
      height={size}
      style={{ height: size, width: "auto" }}
      className={className}
      onError={() => setFailed(true)}
      draggable={false}
    />
  );
}
